import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import {
  getLoan,
  payInstallment,
  reverseInstallmentPayment,
  deleteLoan,
  type LoanResponse,
  type InstallmentResponse,
} from "../../services/loans.service";
import { getClient, type ClientResponse } from "../../services/clients.service";

function formatCurrency(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

export function LoanDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loan, setLoan] = useState<LoanResponse | null>(null);
  const [client, setClient] = useState<ClientResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingId, setPayingId] = useState<string | null>(null);
  const [showPayModal, setShowPayModal] = useState<InstallmentResponse | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reverseTarget, setReverseTarget] = useState<InstallmentResponse | null>(null);
  const [reverseMotivo, setReverseMotivo] = useState("");
  const [reversing, setReversing] = useState(false);

  const canPay = hasPermission("parcelas:registrar_pagamento");
  const canDelete = hasPermission("emprestimos:excluir");

  const STATUS_MAP: Record<string, { label: string; class: string }> = {
    pendente: { label: t("loans.status_pending"), class: "bg-warning/10 text-warning" },
    ativo: { label: t("loans.status_active"), class: "bg-primary-100 text-primary-700" },
    finalizado: { label: t("loans.status_finished"), class: "bg-success/10 text-success" },
    inadimplente: { label: t("loans.status_defaulted"), class: "bg-error/10 text-error" },
  };

  const fetchLoan = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const loanData = await getLoan(id);
      setLoan(loanData);
      try {
        const clientData = await getClient(loanData.clienteId);
        setClient(clientData);
      } catch {
        // Client may not be accessible
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("loans.detail_load_error"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchLoan();
  }, [fetchLoan]);

  const handlePay = async (formaPagamento: string, observacao?: string) => {
    if (!showPayModal) return;
    setPayingId(showPayModal.id);
    try {
      await payInstallment(showPayModal.id, {
        formaPagamento,
        observacao,
      });
      setShowPayModal(null);
      fetchLoan();
    } catch (err) {
      alert(err instanceof Error ? err.message : t("loans.detail_pay_error"));
    } finally {
      setPayingId(null);
    }
  };

  const handleReverse = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    try {
      await reverseInstallmentPayment(reverseTarget.id, {
        motivo: reverseMotivo.trim() || undefined,
      });
      setReverseTarget(null);
      setReverseMotivo("");
      fetchLoan();
    } catch (err) {
      alert(err instanceof Error ? err.message : t("loans.detail_reverse_error"));
    } finally {
      setReversing(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteLoan(id);
      navigate("/dashboard/emprestimos");
    } catch (err) {
      alert(err instanceof Error ? err.message : t("loans.detail_cancel_error"));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="rounded-lg bg-error/10 p-4 text-error">
        {error || t("loans.detail_not_found")}
      </div>
    );
  }

  const statusInfo = STATUS_MAP[loan.status] || { label: loan.status, class: "bg-muted text-muted-foreground" };
  const parcelas = loan.parcelas || [];
  const parcelasPagas = parcelas.filter((p) => p.pago).length;
  const saldoDevedor = parcelas
    .filter((p) => !p.pago)
    .reduce((sum, p) => sum + parseFloat(p.valor), 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard/emprestimos")}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <BackIcon />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("loans.detail_title", { value: formatCurrency(loan.valor) })}
            </h1>
            {client && (
              <p className="text-muted-foreground">
                {t("loans.detail_client", { name: client.nome })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-sm font-medium ${statusInfo.class}`}>
            {statusInfo.label}
          </span>
          {canDelete && loan.status === "ativo" && (
            <Button
              variant="outline"
              size="sm"
              className="text-error hover:bg-error/10"
              onClick={() => setShowDeleteConfirm(true)}
            >
              {t("loans.detail_cancel")}
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t("loans.detail_loaned")}</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(loan.valor)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t("loans.detail_total")}</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(loan.valorTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t("loans.detail_paid_installments")}</p>
            <p className="text-2xl font-bold text-foreground">
              {parcelasPagas}/{loan.numParcelas}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t("loans.detail_balance")}</p>
            <p className="text-2xl font-bold text-foreground">{formatCurrency(saldoDevedor)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Installments table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("loans.detail_installments_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_number")}</th>
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_value")}</th>
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_due_date")}</th>
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_pay_date")}</th>
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_status")}</th>
                  <th className="pb-3 text-left text-sm font-medium text-muted-foreground">{t("loans.detail_col_method")}</th>
                  {canPay && (
                    <th className="pb-3 text-right text-sm font-medium text-muted-foreground">{t("loans.detail_col_action")}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {parcelas.map((parcela) => {
                  // Comparar so a data (YYYY-MM-DD), sem hora. `new Date("2026-07-16")`
                  // vira UTC-00:00, que em BRT (UTC-3) vira 15/07 21:00 e faz uma parcela
                  // que vence dia 16 parecer atrasada no fim da tarde do dia 15. Comparo a
                  // string ISO com a string local (YYYY-MM-DD) direto — ordena
                  // lexicograficamente igual a data e independe de timezone.
                  const todayIso = (() => {
                    const d = new Date();
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    return `${y}-${m}-${day}`;
                  })();
                  const isOverdue = !parcela.pago && parcela.dataVencimento < todayIso;
                  return (
                    <tr key={parcela.id} className="hover:bg-muted/50">
                      <td className="py-3 text-foreground font-medium">{parcela.numero}</td>
                      <td className="py-3 text-foreground">{formatCurrency(parcela.valor)}</td>
                      <td className="py-3 text-muted-foreground text-sm">
                        {formatDate(parcela.dataVencimento)}
                      </td>
                      <td className="py-3 text-muted-foreground text-sm">
                        {parcela.dataPagamento ? formatDate(parcela.dataPagamento) : "-"}
                      </td>
                      <td className="py-3">
                        {parcela.pago ? (
                          <span className="rounded-full bg-success/10 px-2 py-1 text-xs font-medium text-success">
                            {t("loans.detail_status_paid")}
                          </span>
                        ) : isOverdue ? (
                          <span className="rounded-full bg-error/10 px-2 py-1 text-xs font-medium text-error">
                            {t("loans.detail_status_overdue")}
                          </span>
                        ) : (
                          <span className="rounded-full bg-warning/10 px-2 py-1 text-xs font-medium text-warning">
                            {t("loans.detail_status_pending")}
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-muted-foreground text-sm capitalize">
                        {parcela.formaPagamento || "-"}
                      </td>
                      {canPay && (
                        <td className="py-3 text-right">
                          {!parcela.pago ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowPayModal(parcela)}
                              loading={payingId === parcela.id}
                            >
                              {t("loans.detail_pay")}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setReverseTarget(parcela); setReverseMotivo(""); }}
                            >
                              {t("loans.detail_reverse")}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pay Modal */}
      {showPayModal && (
        <PayModal
          parcela={showPayModal}
          onClose={() => setShowPayModal(null)}
          onPay={handlePay}
          loading={!!payingId}
        />
      )}

      {/* Reverse Payment Modal */}
      {reverseTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-lg">
            <h3 className="text-lg font-bold text-foreground">
              {t("loans.detail_reverse_title")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("loans.detail_reverse_desc", {
                numero: reverseTarget.numero,
                valor: formatCurrency(reverseTarget.valor),
              })}
            </p>
            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t("loans.detail_reverse_motivo_label")}
              </label>
              <textarea
                value={reverseMotivo}
                onChange={(e) => setReverseMotivo(e.target.value)}
                rows={3}
                maxLength={255}
                placeholder={t("loans.detail_reverse_motivo_placeholder")}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setReverseTarget(null); setReverseMotivo(""); }}
                disabled={reversing}
              >
                {t("common.cancel")}
              </Button>
              <Button
                className="bg-error hover:bg-error/90"
                onClick={handleReverse}
                loading={reversing}
              >
                {t("loans.detail_reverse_confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-card p-6 shadow-lg">
            <h3 className="text-lg font-bold text-foreground">{t("loans.detail_cancel_title")}</h3>
            <p className="mt-2 text-muted-foreground">
              {t("loans.detail_cancel_message")}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                {t("loans.detail_cancel_back")}
              </Button>
              <Button className="bg-error hover:bg-error/90" onClick={handleDelete}>
                {t("loans.detail_cancel_confirm")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pay Modal
interface PayModalProps {
  parcela: InstallmentResponse;
  onClose: () => void;
  onPay: (formaPagamento: string, observacao?: string) => void;
  loading: boolean;
}

function PayModal({ parcela, onClose, onPay, loading }: PayModalProps) {
  const { t } = useTranslation();
  const [formaPagamento, setFormaPagamento] = useState("pix");
  const [observacao, setObservacao] = useState("");

  const selectClass = "w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-lg bg-card p-6 shadow-lg">
        <h3 className="text-lg font-bold text-foreground">{t("loans.detail_pay_title")}</h3>
        <p className="mt-1 text-muted-foreground">
          {t("loans.detail_pay_subtitle", { number: parcela.numero, value: formatCurrency(parcela.valor) })}
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("loans.detail_pay_method")}
            </label>
            <select
              value={formaPagamento}
              onChange={(e) => setFormaPagamento(e.target.value)}
              className={selectClass}
            >
              <option value="pix">{t("loans.detail_pay_method_pix")}</option>
              <option value="dinheiro">{t("loans.detail_pay_method_cash")}</option>
              <option value="transferencia">{t("loans.detail_pay_method_transfer")}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t("loans.detail_pay_note")}
            </label>
            <input
              type="text"
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              className={selectClass}
              placeholder={t("loans.detail_pay_note_placeholder")}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => onPay(formaPagamento, observacao || undefined)}
            loading={loading}
          >
            {t("loans.detail_pay_confirm")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}
